import { useState } from 'react';
import { theme } from '../theme.js';
import { useEquipmentPlacements, useMoveEquipment, usePlaces } from '../lib/queries.js';
import type { Placement } from '../lib/queries.js';
import { Icon } from './Icon.js';

/**
 * "Where it is": every place this equipment is kept, each with a one-step
 * "Move…" to any asset section or storage location. Pooled items can move
 * part of their quantity; tracked items move whole.
 */
export function EquipmentPlacements({
  orgId, equipmentId, tracked,
}: { orgId: string; equipmentId: string; tracked: boolean }) {
  const { data: placements = [], isLoading } = useEquipmentPlacements(orgId, equipmentId);
  const [moving, setMoving] = useState<string | null>(null); // key of the placement being moved

  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 700, color: theme.textDim, marginBottom: 6 }}>Where it is</div>
      {isLoading && <Muted>Loading…</Muted>}
      {!isLoading && placements.length === 0 && (
        <Muted>Not placed anywhere — add it from an asset’s layout or a storage location.</Muted>
      )}
      {placements.map((p) => {
        const key = p.sectionId ?? p.locationId!;
        return (
          <div key={key} style={{ borderTop: `1px solid ${theme.border}`, padding: '8px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icon name={p.sectionId ? 'truck' : 'pin'} size={14} style={{ color: theme.textMuted, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0, fontSize: 13, color: theme.text }}>
                {p.label}{!tracked && <span style={{ color: theme.textMuted }}> · × {p.quantity}</span>}
              </div>
              {moving !== key && (
                <button onClick={() => setMoving(key)} style={smallBtn}>Move…</button>
              )}
            </div>
            {moving === key && (
              <MoveForm orgId={orgId} equipmentId={equipmentId} from={p} tracked={tracked}
                onDone={() => setMoving(null)} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function MoveForm({ orgId, equipmentId, from, tracked, onDone }: {
  orgId: string; equipmentId: string; from: Placement; tracked: boolean; onDone: () => void;
}) {
  const { data: places } = usePlaces(orgId, true);
  const moveMut = useMoveEquipment(orgId, equipmentId);
  const [dest, setDest] = useState('');               // "s:<sectionId>" | "l:<locationId>"
  const [qty, setQty] = useState(String(from.quantity));
  const fromKey = from.sectionId ? `s:${from.sectionId}` : `l:${from.locationId}`;

  // Group sections by asset for the picker.
  const byAsset = new Map<string, { name: string; sections: { id: string; path: string }[] }>();
  for (const s of places?.sections ?? []) {
    const g = byAsset.get(s.assetId) ?? { name: s.assetName, sections: [] };
    g.sections.push({ id: s.sectionId, path: s.path });
    byAsset.set(s.assetId, g);
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!dest) return;
    const [kind, id] = [dest.slice(0, 1), dest.slice(2)];
    moveMut.mutate({
      from: from.sectionId ? { sectionId: from.sectionId } : { locationId: from.locationId! },
      to: kind === 's' ? { sectionId: id } : { locationId: id },
      quantity: tracked ? undefined : Math.min(Math.max(parseInt(qty, 10) || 1, 1), from.quantity),
    }, { onSuccess: onDone });
  }

  return (
    <form onSubmit={submit} style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
      <select value={dest} onChange={(e) => setDest(e.target.value)} required aria-label="Move to"
        style={{ ...input, flex: '1 1 220px' }}>
        <option value="">{places ? 'Move to…' : 'Loading places…'}</option>
        {[...byAsset.values()].map((g) => (
          <optgroup key={g.name} label={g.name}>
            {g.sections.map((s) => (
              <option key={s.id} value={`s:${s.id}`} disabled={`s:${s.id}` === fromKey}>{s.path}</option>
            ))}
          </optgroup>
        ))}
        {!!places?.locations.length && (
          <optgroup label="Storage locations">
            {places.locations.map((l) => (
              <option key={l.locationId} value={`l:${l.locationId}`} disabled={`l:${l.locationId}` === fromKey}>{l.name}</option>
            ))}
          </optgroup>
        )}
      </select>
      {!tracked && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: theme.textDim }}>
          Qty
          <input type="number" min={1} max={from.quantity} value={qty} onChange={(e) => setQty(e.target.value)}
            style={{ ...input, width: 64 }} aria-label={`Quantity to move (of ${from.quantity})`} />
        </label>
      )}
      <button type="button" onClick={onDone} style={smallBtn}>Cancel</button>
      <button type="submit" disabled={!dest || moveMut.isPending}
        style={{ ...smallBtn, background: theme.accent, color: theme.accentInk, border: 'none' }}>
        {moveMut.isPending ? 'Moving…' : 'Move'}
      </button>
    </form>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 13, color: theme.textMuted, padding: '4px 0' }}>{children}</div>;
}

const input: React.CSSProperties = {
  padding: '7px 9px', background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 8,
  fontSize: 13, color: theme.text, fontFamily: theme.font, outline: 'none', boxSizing: 'border-box',
};

const smallBtn: React.CSSProperties = {
  padding: '7px 10px', borderRadius: 8, flexShrink: 0,
  background: theme.surface, border: `1px solid ${theme.border}`,
  color: theme.textDim, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: theme.font,
};
